import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Grid,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar
} from "@mui/material";
import {
  RefreshCw,
  Calendar,
  Layers,
  Activity,
  AlertOctagon,
  ArrowLeft,
  Clock,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  X,
  ShieldAlert,
  Info,
  CheckCircle,
  FileText,
  Sparkles,
  DollarSign
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Tooltip as RechartsTooltip
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const CpoMultiProjectDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || localStorage.getItem("accessToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectsData, setProjectsData] = useState([]);

  // State for Risks & Issues Modal & Toggle
  const [activeMetricView, setActiveMetricView] = useState("probability"); // "probability" | "impact"
  const [selectedRiskProject, setSelectedRiskProject] = useState(null);
  const [riskModalMetricFilter, setRiskModalMetricFilter] = useState("ALL");
  const [selectedBudgetProject, setSelectedBudgetProject] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);

  // State for Project-Level AI Mitigation Modal (exactly as in CpoPpmDashboard)
  const [selectedProjectForMitigation, setSelectedProjectForMitigation] = useState(null);
  const [aiMitigationModalOpen, setAiMitigationModalOpen] = useState(false);
  const [aiMitigationLoading, setAiMitigationLoading] = useState(false);
  const [aiMitigationError, setAiMitigationError] = useState(null);
  const [aiMitigationData, setAiMitigationData] = useState(null);
  const [toastAlert, setToastAlert] = useState({ open: false, message: "", severity: "info" });

  const getAuthHeaders = () => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  const handleOpenProjectMitigation = async (project) => {
    try {
      setSelectedProjectForMitigation(project);
      setAiMitigationModalOpen(true);
      setAiMitigationLoading(true);
      setAiMitigationError(null);
      setAiMitigationData(null);

      const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
      const projectId = project.projectId || project.id;

      const res = await axios.post(
        `${API_URL}/api/cpo-ppm-dashboard/project/${projectId}/ai-mitigation`,
        { customReason: project.reason || "" },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );

      if (res.data?.success) {
        setAiMitigationData(res.data.data);
      } else {
        throw new Error(res.data?.message || "Failed to generate AI mitigation plan.");
      }
    } catch (err) {
      //console.error("AI Mitigation Error:", err);
      const errorData = err.response?.data;

      // 1. IF PLAN DOES NOT INCLUDE AI (e.g. Basic Plan with 0 tokens)
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        const msg = errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.";
        setAiMitigationError({
          type: "upgrade",
          message: msg,
          severity: "warning"
        });
        setToastAlert({
          open: true,
          message: msg,
          severity: "warning"
        });
        window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Project Risk Mitigation" } }));
      } 
      // 2. IF TOKENS ARE EXHAUSTED
      else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        const msg = `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`;
        setAiMitigationError({
          type: "exhausted",
          message: msg,
          severity: "warning"
        });
        setToastAlert({
          open: true,
          message: msg,
          severity: "warning"
        });
      } 
      else {
        const msg = errorData?.message || err.message || "Failed to generate AI recommendations.";
        setAiMitigationError({
          type: "error",
          message: msg,
          severity: "error"
        });
        setToastAlert({
          open: true,
          message: msg,
          severity: "error"
        });
      }
    } finally {
      setAiMitigationLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/api/cpo-multi-project/health`, {
        ...getAuthHeaders(),
        withCredentials: true
      });

      let companyId = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        companyId = payload.company_id || payload.company_registration_no;
      } catch (e) {}

      if (companyId) {
        axios.get(`${API_URL}/api/cmp/${companyId}`, getAuthHeaders())
          .then(res => {
            if (res.data?.data) setCompanyConfig(res.data.data);
          })
          .catch(() => {});
      }

      if (response.data?.success) {
        const rawData = response.data.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const normalizedData = rawData.map((p) => {
          const progress = p.progress?.percentage !== undefined ? p.progress.percentage : 0;
          const rawStatusLower = String(p.status || "").toLowerCase().trim();
          const isCompleted = progress >= 100 || ["completed", "closed", "done", "finished"].includes(rawStatusLower);

          let calculatedStatus = "On Track";

          if (isCompleted) {
            calculatedStatus = "Completed";
          } else {
            let isEndDatePassed = false;
            let daysRemaining = null;

            if (p.timeline?.endDate && p.timeline.endDate !== "Not specified") {
              const endDateObj = new Date(p.timeline.endDate);
              if (!isNaN(endDateObj.getTime())) {
                endDateObj.setHours(23, 59, 59, 999);
                if (endDateObj < today) {
                  isEndDatePassed = true;
                } else {
                  daysRemaining = (endDateObj - today) / (1000 * 3600 * 24);
                }
              }
            }

            if (isEndDatePassed) {
              calculatedStatus = "Overdue";
            } else if (rawStatusLower.includes("delay") || rawStatusLower.includes("behind")) {
              calculatedStatus = "Delayed";
            } else if (daysRemaining !== null && daysRemaining <= 5 && progress < 95) {
              calculatedStatus = "At Risk";
            } else if (rawStatusLower.includes("hold") || rawStatusLower.includes("pause")) {
              calculatedStatus = "On Hold";
            } else {
              calculatedStatus = "On Track";
            }
          }

          return {
            ...p,
            status: calculatedStatus
          };
        });

        setProjectsData(normalizedData);
      } else {
        throw new Error(response.data?.message || "Failed to load multi-project metrics");
      }
    } catch (err) {
      //console.error("CPO Multi-Project Dashboard error:", err);
      setError(err.message || "Failed to fetch dashboard metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Summary KPI Calculations
  const totalProjects = projectsData.length;
  const avgProgress = totalProjects > 0 
    ? Math.round(projectsData.reduce((acc, p) => acc + (p.progress?.percentage || 0), 0) / totalProjects) 
    : 0;
  const totalPlannedHours = projectsData.reduce((acc, p) => acc + (p.hours?.planned || 0), 0);
  const totalActualHours = projectsData.reduce((acc, p) => acc + (p.hours?.actual || 0), 0);
  const totalExtremeRisks = projectsData.reduce((acc, p) => acc + (p.risksAndIssues?.extreme || 0), 0);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="85vh" bgcolor="#f8fafc">
        <CircularProgress size={56} thickness={4.5} style={{ color: "#2563EB" }} />
        <Typography variant="h6" sx={{ mt: 2.5, color: "#1e293b", fontWeight: 700 }}>
          Loading Portfolio Health Dashboard...
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
          Aggregating real-time project metrics, hours, and issue statuses
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} bgcolor="#f8fafc" minHeight="100vh">
        <Alert
          severity="error"
          sx={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(239, 68, 68, 0.1)" }}
          action={
            <Button color="inherit" size="small" onClick={fetchDashboardData} startIcon={<RefreshCw size={16} />}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh", p: { xs: 2, md: 4 } }}>
      
      {/* Header section */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
         
         
          <Box>
            <Typography variant="h4" fontWeight={800} color="#0f172a" letterSpacing="-0.02em">
              Multi-Project Dashboard
            </Typography>
            <Typography variant="body2" color="#64748b" sx={{ mt: 0.3 }}>
              Executive side-by-side view tracking timelines, progress completion, hours expenditure, and risk severity.
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          <Tooltip title="Refresh Real-Time Data">
            <Button
              onClick={fetchDashboardData}
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              sx={{
                backgroundColor: "#ffffff",
                borderColor: "#cbd5e1",
                color: "#334155",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "10px",
                px: 2,
                py: 1,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" }
              }}
            >
              Refresh Metrics
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Board Matrix Container with Sticky Left KPI Row Column */}
      <Box sx={{ display: "flex", overflowX: "auto", pb: 3, pt: 1, px: 0.5, borderRadius: "16px" }}>
        
        {/* Sticky Left Row Labels Column */}
        <Paper
          elevation={0}
          sx={{
            flex: "0 0 190px",
            minWidth: "190px",
            position: "sticky",
            left: 0,
            zIndex: 10,
            borderRadius: "16px 0 0 16px",
            border: "1px solid #cbd5e1",
            borderRight: "2px solid #94a3b8",
            backgroundColor: "#f1f5f9",
            display: "flex",
            flexDirection: "column",
            boxShadow: "4px 0 10px rgba(0,0,0,0.03)"
          }}
        >
          {/* Header Row Label */}
          <Box sx={{ height: "135px", p: 2.5, display: "flex", alignItems: "center", borderBottom: "1px solid #cbd5e1" }}>
            <Typography variant="subtitle2" fontWeight={800} color="#0f172a" textTransform="uppercase" letterSpacing="0.05em">
              Project ▸<br /><Typography component="span" variant="caption" color="#64748b" fontWeight={700}>KPI Breakdown ▾</Typography>
            </Typography>
          </Box>

          {/* Progress Row Label */}
          <Box sx={{ height: "230px", p: 2.5, display: "flex", flexDirection: "column", justifyContent: "center", borderBottom: "1px solid #cbd5e1" }}>
            <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
              Progress
            </Typography>
            <Typography variant="caption" color="#64748b">
              Completion Rate & Active Work
            </Typography>
          </Box>

          {/* Planned vs Actual Hours Row Label */}
          <Box sx={{ height: "220px", p: 2.5, display: "flex", flexDirection: "column", justifyContent: "center", borderBottom: "1px solid #cbd5e1" }}>
            <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
              Planned Vs Actual Hours
            </Typography>
            <Typography variant="caption" color="#64748b">
              Estimated vs Logged Effort
            </Typography>
          </Box>


          {/* Risks & Issues Row Label */}
          <Box sx={{ flexGrow: 1, p: 2.5, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
              Risks & Issues
            </Typography>
            <Typography variant="caption" color="#64748b">
              Open items by Risk Probability
            </Typography>
          </Box>
        </Paper>

        {/* Dynamic Side-by-Side Project Columns */}
        <Box sx={{ display: "flex", gap: 2.5, pl: 2.5 }}>
          {projectsData.map((project) => {
            const progressPieData = [
              { name: "Completed", value: project.progress.percentage, color: "#2563eb" },
              { name: "Remaining", value: Math.max(100 - project.progress.percentage, 0), color: "#e2e8f0" }
            ];

            const risksBarData = [
              { name: "High", value: project.risksAndIssues?.probability?.high || 0, color: "#ef4444" },
              { name: "Medium", value: project.risksAndIssues?.probability?.medium || 0, color: "#f97316" },
              { name: "Low", value: project.risksAndIssues?.probability?.low || 0, color: "#2563eb" }
            ];

            const isOverdue = project.status === "Overdue";
            const isDelayed = isOverdue || project.status === "Delayed" || project.status === "Behind Schedule";
            const isAtRisk = project.status === "At Risk" || project.status === "Needs Attention";
            const isCompleted = project.status === "Completed" || project.status === "Closed" || project.status === "Done";

            const headerBg = isDelayed ? "#fff1f2" : isAtRisk ? "#fffbeb" : isCompleted ? "#f8fafc" : "#f0fdf4";
            const headerBorder = isDelayed ? "#fecdd3" : isAtRisk ? "#fde68a" : isCompleted ? "#cbd5e1" : "#bbf7d0";
            const headerTextColor = isDelayed ? "#9f1239" : isAtRisk ? "#92400e" : isCompleted ? "#475569" : "#166534";
            const badgeBg = isDelayed ? "#ffe4e6" : isAtRisk ? "#fef3c7" : isCompleted ? "#e2e8f0" : "#dcfce7";

            return (
              <Paper 
                key={project.projectId}
                elevation={0}
                sx={{
                  flex: "0 0 340px",
                  minWidth: "340px",
                  borderRadius: "16px",
                  border: "1px solid #cbd5e1",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  boxShadow: "0 10px 15px -3px rgba(0,0,0,0.04), 0 4px 6px -4px rgba(0,0,0,0.02)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.08)"
                  }
                }}
              >
                {/* Row 1: Project Header */}
                <Box 
                  sx={{ 
                    height: "135px",
                    p: 2.5, 
                    backgroundColor: headerBg, 
                    borderBottom: `1px solid ${headerBorder}`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between"
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: "#475569", letterSpacing: "0.05em" }}>
                        {project.projectKey}
                      </Typography>
                      {(project.budgetRevision?.isHoursOverrun || (project.hours?.actual > project.hours?.planned && project.hours?.planned > 0)) && (
                        <Tooltip title="Click to view Budget Revision & Overrun Details" arrow>
                          <Chip
                            label="Budget Revised"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBudgetProject(project);
                            }}
                            sx={{
                              height: "19px",
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              backgroundColor: "#fef3c7",
                              color: "#b45309",
                              border: "1px solid #fde68a",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: "#fde68a",
                                transform: "scale(1.05)",
                                boxShadow: "0 2px 6px rgba(217, 119, 6, 0.2)"
                              }
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Tooltip title="Click to view AI Risk Mitigation & Action Plan" arrow>
                      <Chip 
                        label={project.status} 
                        size="small" 
                        onClick={() => handleOpenProjectMitigation(project)}
                        sx={{ 
                          fontWeight: 800, 
                          fontSize: "0.7rem", 
                          backgroundColor: badgeBg, 
                          color: headerTextColor,
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            opacity: 0.85,
                            transform: "scale(1.05)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                          }
                        }} 
                      />
                    </Tooltip>
                  </Box>

                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 800, 
                      color: "#0f172a", 
                      fontSize: "1.1rem",
                      whiteSpace: "nowrap", 
                      overflow: "hidden", 
                      textOverflow: "ellipsis" 
                    }}
                  >
                    {project.projectName}
                  </Typography>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.68rem" }}>START</Typography>
                      <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 700, fontSize: "0.82rem" }}>{project.timeline.startDate}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.68rem" }}>END</Typography>
                      <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 700, fontSize: "0.82rem" }}>{project.timeline.endDate}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Row 2: Progress Radial/Donut */}
                <Box sx={{ height: "230px", p: 2.5, borderBottom: "1px solid #f1f5f9", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <Box sx={{ position: "relative", width: 130, height: 130, mb: 1.5 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={progressPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={48}
                          outerRadius={62}
                          startAngle={90}
                          endAngle={-270}
                          dataKey="value"
                        >
                          {progressPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        top: 0, 
                        left: 0, 
                        width: "100%", 
                        height: "100%", 
                        display: "flex", 
                        flexDirection: "column", 
                        alignItems: "center", 
                        justifyContent: "center" 
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                        {project.progress.percentage}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase" }}>
                        Progress
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Row 3: Planned Vs Actual Hours Bar Chart */}
                {(() => {
                  const cardPlannedHrs = project.hours?.planned || 0;
                  const cardActualHrs = project.hours?.actual || 0;
                  const cardBufferHrs = Number(project.hours?.buffer || project.bufferHours || project.budgetRevision?.bufferHours || 0);

                  const hoursChartData = [
                    {
                      name: "Hours",
                      Planned: cardPlannedHrs,
                      ...(cardBufferHrs > 0 ? { "Buffer Hours": cardBufferHrs } : {}),
                      Actual: cardActualHrs
                    }
                  ];

                  return (
                    <Box sx={{ height: "220px", p: 2.5, borderBottom: "1px solid #f1f5f9", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <Box sx={{ height: 130, width: "100%" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={hoursChartData}
                            margin={{ top: 15, right: 10, left: 5, bottom: 0 }}
                          >
                            <XAxis dataKey="name" hide />
                            <YAxis 
                              stroke="#64748b" 
                              fontSize={10} 
                              width={48} 
                              tickFormatter={(val) => {
                                if (val >= 1000) {
                                  const k = val / 1000;
                                  return k % 1 === 0 ? `${k}k` : `${k.toFixed(1)}k`;
                                }
                                return val;
                              }} 
                            />
                            <RechartsTooltip 
                              contentStyle={{ 
                                backgroundColor: "#ffffff", 
                                borderRadius: "10px", 
                                border: "1px solid #cbd5e1", 
                                boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" 
                              }}
                              labelStyle={{ color: "#0f172a", fontWeight: 800 }}
                              itemStyle={{ fontSize: "12px", fontWeight: 700 }}
                              formatter={(val, name) => [`${val} hrs`, name]}
                            />
                            <Bar 
                              dataKey="Planned" 
                              fill="#0f172a" 
                              stackId="plannedStack" 
                              radius={cardBufferHrs > 0 ? [0, 0, 0, 0] : [4, 4, 0, 0]} 
                              barSize={24} 
                            />
                            {cardBufferHrs > 0 && (
                              <Bar 
                                dataKey="Buffer Hours" 
                                fill="#ef4444" 
                                stackId="plannedStack" 
                                radius={[4, 4, 0, 0]} 
                                barSize={24} 
                              />
                            )}
                            <Bar 
                              dataKey="Actual" 
                              fill="#0284c7" 
                              radius={[4, 4, 0, 0]} 
                              barSize={24} 
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ bgcolor: "#f8fafc", p: 1.2, borderRadius: "10px", border: "1px solid #e2e8f0", flexWrap: "wrap", gap: 0.8 }}>
                        <Box display="flex" alignItems="center" gap={0.8}>
                          <Box sx={{ width: 10, height: 10, backgroundColor: "#0f172a", borderRadius: "2px" }} />
                          <Typography variant="caption" sx={{ color: "#334155", fontWeight: 800 }}>
                            Planned: {cardPlannedHrs}
                          </Typography>
                        </Box>
                        {cardBufferHrs > 0 && (
                          <Box display="flex" alignItems="center" gap={0.8}>
                            <Box sx={{ width: 10, height: 10, backgroundColor: "#ef4444", borderRadius: "2px" }} />
                            <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 800 }}>
                              Buffer: +{cardBufferHrs}
                            </Typography>
                          </Box>
                        )}
                        <Box display="flex" alignItems="center" gap={0.8}>
                          <Box sx={{ width: 10, height: 10, backgroundColor: "#0ea5e9", borderRadius: "2px" }} />
                          <Typography variant="caption" sx={{ color: "#0369a1", fontWeight: 800 }}>
                            Actual: {cardActualHrs}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Row 4: Risks & Issues Horizontal Bar Chart (Clickable for Reason & Mitigation Details) */}
                <Box 
                  sx={{ flexGrow: 1, p: 2.5, display: "flex", flexDirection: "column", justifyContent: "space-between" }}
                >
                  <Box 
                    onClick={() => {
                      setSelectedRiskProject(project);
                      setRiskModalMetricFilter("ALL");
                    }}
                    sx={{ 
                      height: 170, 
                      width: "100%", 
                      cursor: "pointer",
                      p: 1,
                      borderRadius: "10px",
                      transition: "background-color 0.2s",
                      "&:hover": { backgroundColor: "#f8fafc" }
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="#64748b" sx={{ display: "block", mb: 0.5 }}>
                      Click bars to view Reason & Mitigation ▾
                    </Typography>
                    <ResponsiveContainer width="100%" height="85%">
                      <BarChart
                        layout="vertical"
                        data={risksBarData}
                        margin={{ top: 5, right: 15, left: -5, bottom: 0 }}
                      >
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} fontWeight={600} width={65} />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: "#ffffff", 
                            borderRadius: "10px", 
                            border: "1px solid #cbd5e1", 
                            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                            padding: "8px 12px"
                          }}
                          labelStyle={{ color: "#0f172a", fontWeight: 800, fontSize: "13px" }}
                          itemStyle={{ color: "#2563eb", fontSize: "12px", fontWeight: 700 }}
                          formatter={(value, name, item) => [`${value} raised items`, `${item.payload.name} Probability Risk`]} 
                        />
                        <Bar 
                          dataKey="value" 
                          radius={[0, 6, 6, 0]} 
                          barSize={16}
                          onClick={(entry) => {
                            setSelectedRiskProject(project);
                            if (entry && entry.name) {
                              setRiskModalMetricFilter(entry.name);
                            } else {
                              setRiskModalMetricFilter("ALL");
                            }
                          }}
                        >
                          {risksBarData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} style={{ cursor: "pointer" }} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Button 
                      variant="outlined"
                      size="small"
                      startIcon={<FileText size={14} />}
                      onClick={() => {
                        setSelectedRiskProject(project);
                        setRiskModalMetricFilter("ALL");
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        borderRadius: "8px",
                        borderColor: "#cbd5e1",
                        color: "#334155",
                        "&:hover": { borderColor: "#2563eb", color: "#2563eb", backgroundColor: "#eff6ff" }
                      }}
                    >
                      View Mitigation Details
                    </Button>

                    <Button 
                      variant="text" 
                      onClick={() => navigate(`/project/${project.projectId}/db`)}
                      endIcon={<ChevronRight size={16} />}
                      sx={{ 
                        textTransform: "none", 
                        fontWeight: 800, 
                        fontSize: "0.82rem", 
                        color: "#2563eb",
                        "&:hover": { backgroundColor: "#eff6ff" }
                      }}
                    >
                      Dashboard
                    </Button>
                  </Box>
                </Box>
              </Paper>
            );
          })}

          {projectsData.length === 0 && (
            <Box sx={{ width: "100%", py: 10, textAlign: "center" }}>
              <Typography variant="h6" color="#64748b">No active projects found in the portfolio.</Typography>
            </Box>
          )}
        </Box>

        {/* Modal Dialog for Budget Revision Details */}
        <Dialog
          open={Boolean(selectedBudgetProject)}
          onClose={() => setSelectedBudgetProject(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px", p: 1 }
          }}
        >
          {selectedBudgetProject && (() => {
            const p = selectedBudgetProject;
            const plannedHrs = p.hours?.planned || 0;
            const actualHrs = p.hours?.actual || 0;
            const bufferHrs = Number(p.hours?.buffer || p.bufferHours || p.budgetRevision?.bufferHours || 0);
            const varianceHrs = Math.round((actualHrs - plannedHrs) * 10) / 10;
            const overrunPct = plannedHrs > 0 ? Math.round((varianceHrs / plannedHrs) * 100) : 0;
            const origBudget = Number(p.budgetRevision?.originalBudget || p.originalBudget || p.budget || 0);
            const revBudget = Number(p.budgetRevision?.revisedBudget || p.revisedBudget || p.budget || 0);
            const budgetDiff = revBudget - origBudget;
            const revisionReason = p.budgetRevision?.reason || p.reason || p.notes || `Actual hours logged (${actualHrs}h) exceeded planned estimate (${plannedHrs}h).`;

            return (
              <>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                  <Box display="flex" alignItems="center" gap={1.2}>
                    <Box sx={{ p: 1, bgcolor: "#fef3c7", borderRadius: "10px", display: "flex" }}>
                      <TrendingUp size={22} color="#d97706" />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#0f172a">
                        Budget Revision Details
                      </Typography>
                      <Typography variant="body2" color="#64748b" fontWeight={600}>
                        {p.projectName} ({p.projectKey})
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={() => setSelectedBudgetProject(null)} size="small">
                    <X size={20} />
                  </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ py: 2.5 }}>
                  {/* Overrun Notice Alert */}
                  <Box 
                    sx={{ 
                      p: 2, 
                      mb: 2.5, 
                      borderRadius: "12px", 
                      bgcolor: "#fffbeb", 
                      border: "1px solid #fde68a",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5
                    }}
                  >
                    <AlertTriangle size={24} color="#d97706" style={{ flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={800} color="#92400e">
                        Effort Overrun: Actual hours exceeded planned hours
                      </Typography>
                      <Typography variant="caption" color="#78350f" fontWeight={600}>
                        Logged work exceeded the original estimate by +{varianceHrs} hrs (+{overrunPct}%).
                      </Typography>
                    </Box>
                  </Box>

                  {/* Effort Comparison Cards */}
                  <Grid container spacing={2} sx={{ mb: 2.5 }}>
                    <Grid item xs={bufferHrs > 0 ? 3 : 4}>
                      <Paper elevation={0} sx={{ p: 1.5, textAlign: "center", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px" }}>
                        <Typography variant="caption" color="#64748b" fontWeight={700} display="block">PLANNED EFFORT</Typography>
                        <Typography variant="h6" fontWeight={800} color="#0f172a">{plannedHrs} <Typography component="span" variant="caption" color="#64748b">hrs</Typography></Typography>
                      </Paper>
                    </Grid>
                    {bufferHrs > 0 && (
                      <Grid item xs={3}>
                        <Paper elevation={0} sx={{ p: 1.5, textAlign: "center", bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px" }}>
                          <Typography variant="caption" color="#b91c1c" fontWeight={700} display="block">BUFFER HOURS</Typography>
                          <Typography variant="h6" fontWeight={800} color="#dc2626">+{bufferHrs} <Typography component="span" variant="caption" color="#dc2626">hrs</Typography></Typography>
                        </Paper>
                      </Grid>
                    )}
                    <Grid item xs={bufferHrs > 0 ? 3 : 4}>
                      <Paper elevation={0} sx={{ p: 1.5, textAlign: "center", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px" }}>
                        <Typography variant="caption" color="#64748b" fontWeight={700} display="block">ACTUAL LOGGED</Typography>
                        <Typography variant="h6" fontWeight={800} color="#0284c7">{actualHrs} <Typography component="span" variant="caption" color="#64748b">hrs</Typography></Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={bufferHrs > 0 ? 3 : 4}>
                      <Paper elevation={0} sx={{ p: 1.5, textAlign: "center", bgcolor: "#fff7ed", border: "1px solid #ffedd5", borderRadius: "10px" }}>
                        <Typography variant="caption" color="#c2410c" fontWeight={700} display="block">VARIANCE</Typography>
                        <Typography variant="h6" fontWeight={800} color="#ea580c">+{varianceHrs} <Typography component="span" variant="caption" color="#ea580c">hrs</Typography></Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Financial Budget Revision Comparison Box */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 2.5,
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      bgcolor: "#ffffff",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.03)"
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} color="#64748b" textTransform="uppercase" letterSpacing="0.05em" display="block" mb={1.5}>
                      Financial Budget Impact
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={5}>
                        <Box sx={{ p: 1.5, bgcolor: "#f1f5f9", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                          <Typography variant="caption" color="#64748b" fontWeight={700} display="block">
                            ORIGINAL BUDGET
                          </Typography>
                          <Typography variant="h6" fontWeight={800} color="#334155">
                            {companyConfig?.currency_symbol || '₹'}{origBudget.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2} textAlign="center">
                        <Typography variant="h5" fontWeight={800} color="#d97706">➔</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Box sx={{ p: 1.5, bgcolor: "#fef3c7", borderRadius: "8px", border: "1px solid #fde68a" }}>
                          <Typography variant="caption" color="#92400e" fontWeight={800} display="block">
                            REVISED BUDGET
                          </Typography>
                          <Typography variant="h6" fontWeight={800} color="#b45309">
                            {companyConfig?.currency_symbol || '₹'}{revBudget.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    {budgetDiff > 0 && (
                      <Typography variant="caption" color="#b45309" fontWeight={700} sx={{ display: "block", mt: 1.5, textAlign: "center" }}>
                        Budget Adjustment: +{companyConfig?.currency_symbol || '₹'}{budgetDiff.toLocaleString()} (+{overrunPct}% due to logged effort)
                      </Typography>
                    )}
                  </Paper>

                  {/* Revision Reason / Justification */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      bgcolor: "#f8fafc"
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <FileText size={16} color="#475569" />
                      <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                        Revision Reason & Justification
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="#334155" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontStyle: revisionReason ? "normal" : "italic" }}>
                      {revisionReason || "No specific justification recorded for this revision."}
                    </Typography>
                  </Paper>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2 }}>
                  <Button
                    onClick={() => setSelectedBudgetProject(null)}
                    variant="contained"
                    sx={{
                      backgroundColor: "#0f172a",
                      color: "#ffffff",
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "8px",
                      px: 3,
                      "&:hover": { backgroundColor: "#334155" }
                    }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

        {/* Modal Dialog for Reasons & Mitigation Details */}
        <Dialog
          open={Boolean(selectedRiskProject)}
          onClose={() => setSelectedRiskProject(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px", p: 1 }
          }}
        >
          {selectedRiskProject && (
            <>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ShieldAlert size={24} color="#ef4444" />
                    <Typography variant="h6" fontWeight={800} color="#0f172a">
                      {selectedRiskProject.projectName} ({selectedRiskProject.projectKey})
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="#64748b">
                    Raised Issues/Bugs, Root Cause Reasons & Mitigation Plans
                  </Typography>
                </Box>
                <IconButton onClick={() => setSelectedRiskProject(null)} size="small">
                  <X size={20} />
                </IconButton>
              </DialogTitle>
              <Divider />
              <DialogContent sx={{ py: 2.5 }}>
                {/* Filter bar */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={1.5}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {["ALL", "High", "Medium", "Low"].map((level) => (
                      <Chip
                        key={level}
                        label={level === "ALL" ? "All Raised Issues" : `${level} Probability`}
                        onClick={() => setRiskModalMetricFilter(level)}
                        color={riskModalMetricFilter === level ? "primary" : "default"}
                        variant={riskModalMetricFilter === level ? "filled" : "outlined"}
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: "6px" }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" fontWeight={700} color="#64748b">
                    Raised Items: {(selectedRiskProject.risksAndIssues?.issuesList || []).length}
                  </Typography>
                </Box>

                {/* List of raised issues */}
                {(() => {
                  const list = selectedRiskProject.risksAndIssues?.issuesList || [];
                  const filtered = list.filter((i) => {
                    if (riskModalMetricFilter === "ALL") return true;
                    return String(i.risk_probability || "").toLowerCase().includes(riskModalMetricFilter.toLowerCase());
                  });

                  if (filtered.length === 0) {
                    return (
                      <Box p={4} textAlign="center" bgcolor="#f8fafc" borderRadius="12px" border="1px dashed #cbd5e1">
                        <Info size={32} color="#94a3b8" />
                        <Typography variant="body1" fontWeight={700} color="#64748b" mt={1}>
                          No raised issues or bugs found for this project selection.
                        </Typography>
                      </Box>
                    );
                  }

                  return (
                    <Stack spacing={2}>
                      {filtered.map((item) => (
                        <Paper
                          key={item.id || item.key}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: "12px",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                            "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} flexWrap="wrap" gap={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={item.key}
                                size="small"
                                sx={{ fontWeight: 800, fontSize: "0.75rem", backgroundColor: "#0f172a", color: "#ffffff", borderRadius: "6px" }}
                              />
                              <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
                                {item.summary}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                              <Chip
                                label={`Prob: ${item.risk_probability || "Medium"}`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.7rem",
                                  backgroundColor: String(item.risk_probability).toLowerCase().includes("high") ? "#ffe4e6" : "#f1f5f9",
                                  color: String(item.risk_probability).toLowerCase().includes("high") ? "#e11d48" : "#334155",
                                  borderRadius: "6px"
                                }}
                              />
                              <Chip
                                label={item.risk_status || item.status || "Identified"}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 700, fontSize: "0.7rem", borderRadius: "6px" }}
                              />
                            </Box>
                          </Box>

                          <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} md={4}>
                              <Box sx={{ bgcolor: "#fff7ed", p: 1.8, borderRadius: "8px", borderLeft: "4px solid #f97316", height: "100%" }}>
                                <Typography variant="caption" fontWeight={800} color="#9a3412" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <AlertTriangle size={14} /> Reason / Root Cause
                                </Typography>
                                <Typography variant="body2" color="#334155" sx={{ mt: 0.8, fontSize: "0.85rem", whiteSpace: "pre-line" }}>
                                  {item.risk_reason && item.risk_reason.trim() !== "" ? item.risk_reason : "No detailed root cause reason was provided when raising this issue."}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box sx={{ bgcolor: "#f0fdf4", p: 1.8, borderRadius: "8px", borderLeft: "4px solid #16a34a", height: "100%" }}>
                                <Typography variant="caption" fontWeight={800} color="#15803d" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <CheckCircle size={14} /> Mitigation Plan
                                </Typography>
                                <Typography variant="body2" color="#166534" sx={{ mt: 0.8, fontSize: "0.85rem", whiteSpace: "pre-line" }}>
                                  {item.risk_mitigation && item.risk_mitigation.trim() !== "" ? item.risk_mitigation : "No mitigation steps logged for this issue."}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box sx={{ bgcolor: "#eff6ff", p: 1.8, borderRadius: "8px", borderLeft: "4px solid #2563eb", height: "100%" }}>
                                <Typography variant="caption" fontWeight={800} color="#1e40af" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Activity size={14} /> Action Item
                                </Typography>
                                <Typography variant="body2" color="#1e3a8a" sx={{ mt: 0.8, fontSize: "0.85rem", whiteSpace: "pre-line" }}>
                                  {item.risk_action && item.risk_action.trim() !== "" ? item.risk_action : "No concrete action step logged."}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  );
                })()}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setSelectedRiskProject(null)} variant="contained" sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", bgcolor: "#0f172a" }}>
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Project-Level AI Risk Mitigation & Action Items Modal (Matches CpoPpmDashboard) */}
        <Dialog
          open={aiMitigationModalOpen}
          onClose={() => setAiMitigationModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px", overflow: "hidden" }
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              px: 3,
              py: 2.5,
              backgroundColor: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "#dc2626" : "#2563eb",
              color: "#ffffff"
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "10px",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Sparkles size={22} color="#ffffff" />
              </Box>
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#ffffff" }}>
                    AI Risk Mitigation & Action Plan
                  </Typography>
                  <Chip
                    label={selectedProjectForMitigation?.status || "At Risk"}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.25)",
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      borderRadius: "6px"
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.8rem" }}>
                  {selectedProjectForMitigation?.projectKey || selectedProjectForMitigation?.key} • {selectedProjectForMitigation?.projectName || selectedProjectForMitigation?.name} ({selectedProjectForMitigation?.progress?.percentage || selectedProjectForMitigation?.overallProgress || 0}% Completed)
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setAiMitigationModalOpen(false)} sx={{ color: "#ffffff" }}>
              <X size={20} />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 3, backgroundColor: "#f8fafc" }}>
            {/* 1. Project & Reported Root Cause Banner */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                border: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "1px solid #fecaca" : "1px solid #fde68a",
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AlertTriangle
                    size={18}
                    color={selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "#dc2626" : "#d97706"}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "#991b1b" : "#92400e",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontSize: "0.75rem"
                    }}
                  >
                    Reported Reason & Root Cause
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  Planned End Date: {selectedProjectForMitigation?.timeline?.endDate || selectedProjectForMitigation?.plannedEndDate || "Not specified"}
                </Typography>
              </Box>

              {/* Root cause text */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "#fef2f2" : "#fffbeb",
                  border: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "1px dashed #fca5a5" : "1px dashed #fcd34d",
                  color: "#1e293b",
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                  fontWeight: 500
                }}
              >
                {selectedProjectForMitigation?.reason || selectedProjectForMitigation?.notes || aiMitigationData?.reason || "No custom reason recorded yet by the project team. AI is analyzing status, timeline variance, and progress metrics."}
              </Box>

              {/* Progress & Timeline Bar */}
              <Box mt={2} display="flex" alignItems="center" gap={2}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, minWidth: 110 }}>
                  Current Progress:
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Number(selectedProjectForMitigation?.progress?.percentage || selectedProjectForMitigation?.overallProgress || 0)}
                  sx={{
                    flexGrow: 1,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#e2e8f0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "#dc2626" : "#d97706"
                    }
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 35, color: "#1e293b" }}>
                  {selectedProjectForMitigation?.progress?.percentage || selectedProjectForMitigation?.overallProgress || 0}%
                </Typography>
              </Box>
            </Paper>

            {/* 2. Loading State */}
            {aiMitigationLoading && (
              <Box
                py={6}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
              >
                <CircularProgress size={45} thickness={4} sx={{ color: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "#dc2626" : "#d97706" }} />
                <Box textAlign="center">
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b" }}>
                    AI is analyzing root cause and generating mitigation plan & action items...
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Synthesizing root cause impediment, timeline variance, and role ownership
                  </Typography>
                </Box>
              </Box>
            )}

            {/* 3. Error State */}
            {aiMitigationError && !aiMitigationLoading && (
              <Alert
                severity={typeof aiMitigationError === "object" ? aiMitigationError.severity : "error"}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      if (typeof aiMitigationError === "object" && aiMitigationError.type === "upgrade") {
                        window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Project Risk Mitigation" } }));
                      } else {
                        handleOpenProjectMitigation(selectedProjectForMitigation);
                      }
                    }}
                    sx={{ fontWeight: 700 }}
                  >
                    {typeof aiMitigationError === "object" && aiMitigationError.type === "upgrade" ? "Upgrade Plan" : "Retry"}
                  </Button>
                }
                sx={{ mb: 2 }}
              >
                {typeof aiMitigationError === "object" ? aiMitigationError.message : aiMitigationError}
              </Alert>
            )}

            {/* 4. AI Generated Results */}
            {aiMitigationData && !aiMitigationLoading && (
              <Box display="flex" flexDirection="column" gap={2.5}>
                {/* Strategic Mitigation Plan */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem", mb: 1.5 }}>
                    🛡️ Strategic Mitigation Plan
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1.2}>
                    {aiMitigationData.mitigationPlan?.map((step, idx) => (
                      <Box
                        key={idx}
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                        sx={{
                          p: 1.5,
                          borderRadius: "8px",
                          backgroundColor: "#f8fafc",
                          border: "1px solid #f1f5f9"
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: "#eff6ff",
                            color: "#2563eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "0.75rem"
                          }}
                        >
                          {idx + 1}
                        </Box>
                        <Typography variant="body2" sx={{ color: "#334155", fontWeight: 500, fontSize: "0.85rem", pt: 0.2 }}>
                          {step}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>

                {/* Concrete Action Items Table */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem", mb: 1.5 }}>
                    🎯 Action Items
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Action Item</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Owner</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Timeline</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Expected Impact</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {aiMitigationData.actionItems?.map((item, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.8rem" }}>
                              {item.action}
                            </TableCell>
                            <TableCell sx={{ color: "#334155", fontSize: "0.8rem", minWidth: 160 }}>
                              <Chip
                                label={item.owner || "Team Lead"}
                                size="small"
                                sx={{
                                  fontSize: "0.75rem",
                                  backgroundColor: "#eff6ff",
                                  color: "#1d4ed8",
                                  fontWeight: 600,
                                  height: "auto",
                                  py: 0.5,
                                  px: 0.5,
                                  "& .MuiChip-label": {
                                    whiteSpace: "normal",
                                    display: "block"
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "#dc2626" : "#d97706", fontWeight: 600, fontSize: "0.75rem" }}>
                              {item.timeline || "Immediate"}
                            </TableCell>
                            <TableCell sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                              {item.impact || "Unblocks project progress"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2.5, backgroundColor: "#ffffff", borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
            <Button
              size="small"
              startIcon={<Sparkles size={16} />}
              onClick={() => handleOpenProjectMitigation(selectedProjectForMitigation)}
              disabled={aiMitigationLoading}
              sx={{ textTransform: "none", fontWeight: 600, color: selectedProjectForMitigation?.status === "Delayed" || selectedProjectForMitigation?.status === "Overdue" ? "#dc2626" : "#d97706" }}
            >
              Regenerate AI Plan
            </Button>
            <Box display="flex" gap={1.5}>
              <Button
                variant="outlined"
                onClick={() => {
                  setAiMitigationModalOpen(false);
                  navigate(`/project/${selectedProjectForMitigation?.projectId || selectedProjectForMitigation?.id}/db`);
                }}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Open Project Workspace
              </Button>
              <Button
                variant="contained"
                onClick={() => setAiMitigationModalOpen(false)}
                sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#2563eb" }}
              >
                Done
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Project Budget Revision & Effort Overrun Details Modal */}
        <Dialog
          open={Boolean(selectedBudgetProject)}
          onClose={() => setSelectedBudgetProject(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px", overflow: "hidden" }
          }}
        >
          {selectedBudgetProject && (() => {
            const bRevision = selectedBudgetProject.budgetRevision || {};
            const plannedHrs = selectedBudgetProject.hours?.planned || 0;
            const actualHrs = selectedBudgetProject.hours?.actual || 0;
            const varianceHrs = bRevision.hoursVariance !== undefined
              ? bRevision.hoursVariance
              : Math.max(0, Math.round((actualHrs - plannedHrs) * 10) / 10);
            const overrunPct = bRevision.overrunPercentage !== undefined
              ? bRevision.overrunPercentage
              : (plannedHrs > 0 ? Math.round(((actualHrs - plannedHrs) / plannedHrs) * 100) : 0);

            const origBudget = bRevision.originalBudget || selectedBudgetProject.originalBudget || selectedBudgetProject.budget || 0;
            const revBudget = bRevision.revisedBudget || selectedBudgetProject.revisedBudget || selectedBudgetProject.budget || 0;
            const budgetDelta = Math.max(0, revBudget - origBudget);
            const justification = bRevision.reason || selectedBudgetProject.reason || selectedBudgetProject.notes || "Actual engineering effort exceeded planned hours allocation due to extended implementation and review cycles.";

            return (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: 3,
                    py: 2.5,
                    backgroundColor: "#b45309",
                    background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    color: "#ffffff"
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: "10px",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <DollarSign size={24} color="#ffffff" />
                    </Box>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.15rem", color: "#ffffff" }}>
                          Budget Revision & Overrun Details
                        </Typography>
                        <Chip
                          label="Budget Revised"
                          size="small"
                          sx={{
                            backgroundColor: "#fef3c7",
                            color: "#92400e",
                            fontWeight: 800,
                            fontSize: "0.72rem",
                            borderRadius: "6px"
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.82rem" }}>
                        {selectedBudgetProject.projectKey} • {selectedBudgetProject.projectName}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={() => setSelectedBudgetProject(null)} sx={{ color: "#ffffff" }}>
                    <X size={20} />
                  </IconButton>
                </Box>

                <DialogContent sx={{ p: 3, backgroundColor: "#f8fafc" }}>
                  <Stack spacing={2.5}>
                    {/* Notice Banner */}
                    <Alert
                      severity="warning"
                      icon={<AlertTriangle size={22} color="#b45309" />}
                      sx={{
                        backgroundColor: "#fffbeb",
                        border: "1px solid #fde68a",
                        color: "#92400e",
                        borderRadius: "10px",
                        "& .MuiAlert-message": { fontSize: "0.85rem", fontWeight: 500 }
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#78350f", mb: 0.3 }}>
                        Labor Effort Overrun Detected (+{varianceHrs} hrs / +{overrunPct}%)
                      </Typography>
                      Actual task logged hours ({actualHrs} hrs) exceeded the baseline planned hours ({plannedHrs} hrs). Project budget adjustment is indicated to reconcile increased labor and resource consumption.
                    </Alert>

                    {/* Effort Breakdown 3 Cards */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: "10px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderLeft: "4px solid #64748b"
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                            Planned Hours
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b", mt: 0.5 }}>
                            {plannedHrs} <Typography component="span" variant="body2" color="#64748b">hrs</Typography>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                            Original approved scope
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: "10px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderLeft: "4px solid #d97706"
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "#d97706", fontWeight: 700, textTransform: "uppercase" }}>
                            Actual Logged Hours
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: "#b45309", mt: 0.5 }}>
                            {actualHrs} <Typography component="span" variant="body2" color="#b45309">hrs</Typography>
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#b45309" }}>
                            Timesheet recorded effort
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: "10px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderLeft: "4px solid #ef4444"
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 700, textTransform: "uppercase" }}>
                            Hours Overrun
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: "#dc2626" }}>
                              +{varianceHrs} <Typography component="span" variant="body2" color="#dc2626">hrs</Typography>
                            </Typography>
                            <Chip
                              label={`+${overrunPct}%`}
                              size="small"
                              sx={{
                                height: "20px",
                                fontSize: "0.68rem",
                                fontWeight: 800,
                                backgroundColor: "#fee2e2",
                                color: "#991b1b"
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: "#dc2626" }}>
                            Variance above planned baseline
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Financial Re-baseline Details */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: "12px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                        <TrendingUp size={18} color="#d97706" />
                        Financial Budget Adjustment
                      </Typography>

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, borderRadius: "8px", backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                              ORIGINAL BASELINE BUDGET
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", mt: 0.5 }}>
                              ${Number(origBudget).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="#94a3b8">
                              Initial approved cap
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, borderRadius: "8px", backgroundColor: "#fffbeb", border: "1px solid #fde68a" }}>
                            <Typography variant="caption" sx={{ color: "#b45309", fontWeight: 700 }}>
                              BUDGET INCREASE / DELTA
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#b45309", mt: 0.5 }}>
                              +${Number(budgetDelta).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="#b45309">
                              +{overrunPct}% effort-adjusted
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, borderRadius: "8px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                            <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 700 }}>
                              REVISED PROJECT BUDGET
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#166534", mt: 0.5 }}>
                              ${Number(revBudget).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="#15803d">
                              {bRevision.hasFormalRevision ? "Formally saved in project" : "Projected adjusted budget"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Justification / Reason */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: "12px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <FileText size={18} color="#2563eb" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                          Revision Justification & Overrun Reason
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0"
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "#334155", fontWeight: 500, lineHeight: 1.6, whiteSpace: "pre-line" }}>
                          {justification}
                        </Typography>
                      </Box>
                    </Paper>
                  </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, backgroundColor: "#ffffff", borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSelectedBudgetProject(null);
                      navigate(`/project/${selectedBudgetProject.projectId}/db`);
                    }}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#475569", borderColor: "#cbd5e1" }}
                  >
                    View Project Dashboard
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setSelectedBudgetProject(null)}
                    sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#b45309", "&:hover": { backgroundColor: "#92400e" } }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

        {/* Global Toast Alert */}
        <Snackbar
          open={toastAlert.open}
          autoHideDuration={6000}
          onClose={() => setToastAlert(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert 
            onClose={() => setToastAlert(prev => ({ ...prev, open: false }))} 
            severity={toastAlert.severity} 
            variant="filled" 
            sx={{ width: "100%", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", fontWeight: 600 }}
          >
            {toastAlert.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default CpoMultiProjectDashboard;

